import { INotificationMessage } from "@fl/shared/src/types/messages";
import { NotificationsLogAttributes } from "@fl/shared/src/types/models/logs/notifications";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import ApiNotificacoesLog from "../services/suporte/auth/logs/notifications";
import StorageItems, { IKeys } from "../storage";
import { useAuth } from "./auth"; // <— se o path for diferente no seu app, ajustar

interface INotificationContext {
  showNotification: boolean;
  notifications: INotificationMessage[];
  setShowNotification: (showNotification: boolean) => void;
  addNotification: (alert: INotificationMessage) => void;
  removeNotification: (key: string) => void;
}

const NotificationContext = createContext<INotificationContext>({
  showNotification: false,
  notifications: [],
  setShowNotification: () => {},
  addNotification: () => {},
  removeNotification: () => {},
});

export const useNotificationContext = () => useContext(NotificationContext);

const storage = new StorageItems();
const STORAGE_KEY: IKeys = "notifications";

// --- NOVO: endpoint para logs (ajuste se seu backend usar outro caminho)
//const LOG_ENDPOINT = `${process.env.REACT_APP_API_URL ?? ""}/logs/notifications/create`;

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { authToken } = useAuth(); // opcional: para enviar user_id / bearer

  const [showNotification, setShowNotification] = useState(false);
  const [notifications, setNotifications] = useState<INotificationMessage[]>(
    () => {
      const storedNotifications = storage.get(STORAGE_KEY);
      return storedNotifications || [];
    }
  );

  // ====== PERSISTÊNCIA LOCAL (inalterado) ======
  useEffect(() => {
    storage.set(STORAGE_KEY, notifications);
  }, [notifications]);

  // ====== NOVO: FILA + FLUSH PARA LOG REMOTO ======
  const queueRef = useRef<NotificationsLogAttributes[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushingRef = useRef(false);

  const mapToLog = (
    alert: INotificationMessage
  ): NotificationsLogAttributes => ({
    key: alert.key,
    header: alert.header,
    message: alert.message ?? null,
    variant: alert.variant,
    show: alert.show,
    client_ts: alert.date ?? new Date(),
    user_id: authToken?.id_usuario ?? null, // ajuste se seu token usa outro campo
    path: (typeof window !== "undefined" && window.location?.pathname) || null,
    app_version: (process.env.REACT_APP_VERSION as string) || null,
    metadata: null,
  });

  const flushQueue = async () => {
    if (flushingRef.current) return;
    if (!queueRef.current.length) return;

    const batch = [...queueRef.current];
    queueRef.current = [];
    flushingRef.current = true;
    try {
      // Usa sua API (com axios, headers e token já configurados)
      await new ApiNotificacoesLog(batch).createNotifications();
    } catch {
      // re-enfileira em caso de falha
      queueRef.current = [...batch, ...queueRef.current];
    } finally {
      flushingRef.current = false;
    }
  };

  const scheduleFlush = () => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      void flushQueue();
    }, 1500); // ajustável
  };

  // (Opcional) tenta flush ao fechar/atualizar a aba
  useEffect(() => {
    const onBeforeUnload = () => {
      // Não dá pra garantir envio com axios aqui; apenas disparamos um flush imediato.
      // Se quiser muito garantir, considere usar sendBeacon no backend sem auth estrita.
      if (queueRef.current.length && !flushingRef.current) {
        // Chamar sem await (evento unload)
        void flushQueue();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // ====== API ORIGINAL (mantida) + enfileirar log ======
  const addNotification = (alert: INotificationMessage) => {
    setNotifications((prevNotifications) => {
      const updatedNotifications = [...prevNotifications, alert];
      storage.set(STORAGE_KEY, updatedNotifications); // Atualiza o localStorage
      return updatedNotifications;
    });

    // NOVO: enfileira log e agenda flush
    try {
      queueRef.current.push(mapToLog(alert));
      scheduleFlush();
    } catch {
      // silencioso — não impacta UX de notificação
    }
  };

  const removeNotification = (key: string) => {
    setNotifications((prevNotifications) => {
      const updatedNotifications = prevNotifications.filter(
        (alert) => alert.key !== key
      );
      storage.set(STORAGE_KEY, updatedNotifications); // Atualiza o localStorage
      return updatedNotifications;
    });
  };

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        notifications,
        setShowNotification,
        addNotification,
        removeNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
